import { Ellipsis, Pencil, Settings, Trash2, UserRound, Users } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { Wordmark } from '../brand/Wordmark'
import { Button } from '../ui/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/DropdownMenu'

export interface RecentItem {
  id: string
  title: string
}

// Inline rename input. Owns its own draft and focuses/selects ONCE on mount —
// a render-time ref callback would re-select on every keystroke and clobber the
// text. Enter or blur commits; Escape cancels.
function RenameRow({
  initial,
  onCommit,
  onCancel
}: {
  initial: string
  onCommit: (next: string) => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState(initial)
  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])
  return (
    <input
      ref={ref}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onCommit(draft)
        if (e.key === 'Escape') onCancel()
      }}
      className="w-full rounded-md border border-accent/50 bg-background px-3 py-2 text-body-sm text-text outline-none"
      aria-label="Session title"
    />
  )
}

interface SidebarProps {
  recent: RecentItem[]
  activeId?: string
  /** Current hash route, for nav-link active state. */
  route?: string
  onSelect?: (id: string) => void
  onRename?: (id: string, title: string) => void
  onDelete?: (id: string) => void
  className?: string
}

const NAV_LINKS: { href: string; label: string; Icon: LucideIcon }[] = [
  { href: '#/account', label: 'Account', Icon: UserRound },
  { href: '#/contacts', label: 'Contacts', Icon: Users },
  { href: '#/settings', label: 'Settings', Icon: Settings }
]

function NavLinks({ route }: { route?: string }) {
  return (
    <ul className="px-2 pt-2 space-y-0.5">
      {NAV_LINKS.map(({ href, label, Icon }) => {
        const active = route?.startsWith(href)
        return (
          <li key={href}>
            <a
              href={href}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-body-sm text-text',
                'transition-colors duration-[120ms] ease-[var(--ease-standard)] hover:bg-accent-tint',
                active && 'bg-accent-tint font-medium'
              )}
            >
              <Icon className="size-4 text-muted-foreground" />
              {label}
            </a>
          </li>
        )
      })}
    </ul>
  )
}

export function Sidebar({
  recent,
  activeId,
  route,
  onSelect,
  onRename,
  onDelete,
  className
}: SidebarProps) {
  return (
    <nav
      className={cn(
        'flex h-full w-[260px] flex-col bg-sidebar border-r border-border',
        className
      )}
      aria-label="Sessions"
    >
      <div className="px-3 h-14 flex items-center">
        <a
          href="#/"
          className="flex items-center rounded-md"
          aria-label="convariance home"
        >
          <Wordmark size="full" />
        </a>
      </div>

      <NavLinks route={route} />

      <RecentList
        recent={recent}
        activeId={activeId}
        onSelect={onSelect}
        onRename={onRename}
        onDelete={onDelete}
      />
    </nav>
  )
}

function RecentList({
  recent,
  activeId,
  onSelect,
  onRename,
  onDelete
}: Pick<
  SidebarProps,
  'recent' | 'activeId' | 'onSelect' | 'onRename' | 'onDelete'
>) {
  // The row being renamed in place.
  const [editingId, setEditingId] = useState<string | null>(null)
  // The session pending a delete confirmation.
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const confirmTitle = recent.find((r) => r.id === confirmId)?.title ?? ''

  return (
    <>
      <div className="px-3 pt-2">
        <p className="px-2 text-label uppercase tracking-[0.02em] text-muted-foreground">
          Recent
        </p>
      </div>
      <ul className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5">
        {recent.length === 0 && (
          <li className="px-3 py-2 text-body-sm text-muted-foreground">
            No recent sessions yet.
          </li>
        )}
        {recent.map((item) =>
          editingId === item.id ? (
            <li key={item.id}>
              <RenameRow
                initial={item.title}
                onCommit={(next) => {
                  if (next.trim()) onRename?.(item.id, next.trim())
                  setEditingId(null)
                }}
                onCancel={() => setEditingId(null)}
              />
            </li>
          ) : (
            <li key={item.id} className="group relative">
              <button
                type="button"
                onClick={() => onSelect?.(item.id)}
                className={cn(
                  'relative w-full rounded-md px-3 py-2 pr-8 text-left text-body-sm text-text',
                  'transition-colors duration-[120ms] ease-[var(--ease-standard)]',
                  'hover:bg-accent-tint',
                  item.id === activeId && 'bg-accent-tint font-medium'
                )}
              >
                {item.id === activeId && (
                  <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-accent" />
                )}
                <span className="line-clamp-1">{item.title}</span>
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Session options"
                      className={cn(
                        'absolute top-1/2 right-1 -translate-y-1/2 opacity-0 transition',
                        'group-hover:opacity-100 focus-visible:opacity-100 data-[popup-open]:opacity-100'
                      )}
                    />
                  }
                >
                  <Ellipsis className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-36">
                  <DropdownMenuItem onClick={() => setEditingId(item.id)}>
                    <Pencil /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => setConfirmId(item.id)}
                  >
                    <Trash2 /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          )
        )}
      </ul>

      <Dialog
        open={confirmId !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmId(null)
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Delete session?</DialogTitle>
            <DialogDescription>
              “{confirmTitle}” will be permanently deleted from this browser. This
              can’t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmId) onDelete?.(confirmId)
                setConfirmId(null)
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
